import React, { useState, useEffect } from 'react';
import { 
  Building, 
  Plus, 
  Search, 
  Filter,
  Edit, 
  Trash2, 
  Archive,
  Globe,
  Phone,
  Mail,
  MoreHorizontal,
  Eye,
  DollarSign,
  Users,
  Palette
} from 'lucide-react';
import { Client } from '../../lib/supabase';
import Button from '../Button';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import ClientBrandSettings from './ClientBrandSettings';

interface CreateClientForm {
  name: string;
  company_name: string;
  email: string;
  phone: string;
  website: string;
  industry: string;
  description: string;
  billing_info: {
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}

const Clients: React.FC = () => {
  const { user, session } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [brandingClient, setBrandingClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [formData, setFormData] = useState<CreateClientForm>({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    website: '',
    industry: '',
    description: '',
    billing_info: {
      address: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    }
  });

  // Fetch clients
  const fetchClients = async () => {
    try {
      let url = `${API_ENDPOINTS.AGENCY.CLIENTS}?`;
      const params = new URLSearchParams();
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await fetch(url + params.toString(), {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data.clients);
      } else {
        console.error('Failed to fetch clients');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [user, searchTerm, statusFilter]);

  // Create client
  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(API_ENDPOINTS.AGENCY.CLIENTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchClients();
        setShowCreateForm(false);
        resetForm();
      } else {
        const error = await response.json();
        console.error('Failed to create client:', error);
      }
    } catch (error) {
      console.error('Error creating client:', error);
    }
  };

  // Update client
  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingClient) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.CLIENTS}/${editingClient.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchClients();
        setEditingClient(null);
        setShowCreateForm(false);
        resetForm();
      } else {
        const error = await response.json();
        console.error('Failed to update client:', error);
      }
    } catch (error) {
      console.error('Error updating client:', error);
    }
  };

  // Archive client
  const handleArchiveClient = async (clientId: string) => {
    if (!window.confirm('Are you sure you want to archive this client?')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.CLIENTS}/${clientId}/archive`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        }
      });

      if (response.ok) {
        await fetchClients();
      } else {
        const error = await response.json();
        console.error('Failed to archive client:', error);
      }
    } catch (error) {
      console.error('Error archiving client:', error);
    }
  };

  // Delete client
  const handleDeleteClient = async (clientId: string) => {
    if (!window.confirm('Are you sure you want to permanently delete this client? This action cannot be undone.')) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.AGENCY.CLIENTS}/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        }
      });

      if (response.ok) {
        await fetchClients();
      } else {
        const error = await response.json();
        console.error('Failed to delete client:', error);
        alert(error.error || 'Failed to delete client');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      company_name: '',
      email: '',
      phone: '',
      website: '',
      industry: '',
      description: '',
      billing_info: {
        address: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      }
    });
  };

  const startEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      company_name: client.company_name || '',
      email: client.email,
      phone: client.phone || '',
      website: client.website || '',
      industry: client.industry || '',
      description: client.description || '',
      billing_info: {
        address: client.billing_info?.address || '',
        city: client.billing_info?.city || '',
        state: client.billing_info?.state || '',
        zip: client.billing_info?.zip || '',
        country: client.billing_info?.country || '',
      }
    });
    setShowCreateForm(true);
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'inactive':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'archived':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (client.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-purple"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Clients</h2>
          <p className="text-gray-600 mt-1">Manage your agency clients and projects</p>
        </div>
        <Button
          onClick={() => setShowCreateForm(true)}
          variant="primary"
          size="medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {editingClient ? 'Edit Client' : 'Add New Client'}
            </h3>
            <Button
              onClick={() => {
                setShowCreateForm(false);
                setEditingClient(null);
                resetForm();
              }}
              variant="tertiary"
              size="small"
            >
              Cancel
            </Button>
          </div>

          <form onSubmit={editingClient ? handleUpdateClient : handleCreateClient} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="Acme Inc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="client@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="Technology, Healthcare, etc."
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                  placeholder="Brief description of the client or project requirements..."
                />
              </div>
            </div>

            {/* Billing Information */}
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Billing Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.billing_info.address}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_info: { ...formData.billing_info, address: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.billing_info.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_info: { ...formData.billing_info, city: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.billing_info.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_info: { ...formData.billing_info, state: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.billing_info.zip}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_info: { ...formData.billing_info, zip: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="10001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.billing_info.country}
                    onChange={(e) => setFormData({
                      ...formData,
                      billing_info: { ...formData.billing_info, country: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-brand-purple"
                    placeholder="United States"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" variant="primary" size="medium">
                {editingClient ? 'Update Client' : 'Add Client'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingClient(null);
                  resetForm();
                }}
                variant="tertiary"
                size="medium"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || statusFilter !== 'all' ? 'No clients found' : 'No clients yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Start adding clients to manage your projects and track mockup deliveries.'
            }
          </p>
          {!searchTerm && statusFilter === 'all' && (
            <Button
              onClick={() => setShowCreateForm(true)}
              variant="primary"
              size="medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Client
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <div key={client.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {client.name}
                    </h3>
                    <span className={getStatusBadge(client.status)}>
                      {client.status}
                    </span>
                  </div>
                  {client.company_name && (
                    <p className="text-sm text-gray-600 mb-1">{client.company_name}</p>
                  )}
                  {client.industry && (
                    <p className="text-xs text-gray-500 mb-3">{client.industry}</p>
                  )}
                </div>
                
                <div className="relative">
                  <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                  {/* Dropdown menu would go here */}
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  <span className="truncate">{client.email}</span>
                </div>
                {client.phone && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="w-4 h-4 mr-2 text-gray-400" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.website && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Globe className="w-4 h-4 mr-2 text-gray-400" />
                    <a 
                      href={client.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-brand-purple hover:underline truncate"
                    >
                      {client.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex justify-between text-sm text-gray-600 mb-4">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  <span>{client.mockups_created} mockups</span>
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 mr-1" />
                  <span>${client.total_revenue.toFixed(2)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 pt-4 border-t border-gray-200">
                <Button
                  onClick={() => startEdit(client)}
                  variant="tertiary"
                  size="small"
                  className="flex-1"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                
                <Button
                  onClick={() => setBrandingClient(client)}
                  variant="tertiary"
                  size="small"
                  className="text-purple-600 hover:text-purple-700"
                >
                  <Palette className="w-4 h-4" />
                </Button>
                
                {client.status !== 'archived' && (
                  <Button
                    onClick={() => handleArchiveClient(client.id)}
                    variant="tertiary"
                    size="small"
                    className="text-yellow-600 hover:text-yellow-700"
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                )}
                
                <Button
                  onClick={() => handleDeleteClient(client.id)}
                  variant="tertiary"
                  size="small"
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Client Brand Settings */}
      {brandingClient && (
        <ClientBrandSettings
          client={brandingClient}
          onBack={() => setBrandingClient(null)}
        />
      )}
    </div>
  );
};

export default Clients;